// Angular
import { Injectable, EventEmitter } from '@angular/core';
// Tranlsation
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { AppConstants } from 'src/app/app.constants';
import { isPolishLanguage } from 'src/app/shared/language-extensions/is-polish-language.language-extension';
import { LanguageType } from '../../../_config/i18n/language-type.model';

export interface Locale {
  lang: string;
  // tslint:disable-next-line:ban-types
  data: Object;
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  // Private properties
  private langIds: any = [];
  private readonly cookieName = '.AspNetCore.Culture';

  constructor(private translate: TranslateService) {
    // add new langIds to the list
    this.translate.addLangs([LanguageType.English]);

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(LanguageType.English);

    // use english as initial language before getting target language from browser/settings
    this.translate.use(LanguageType.English);
  }

  loadTranslations(...args: Locale[]): void {
    const locales = [...args];

    locales.forEach(locale => {
      // use setTranslation() with the third argument set to true
      // to append translations instead of replacing them
      this.translate.setTranslation(locale.lang, locale.data, true);

      this.langIds.push(locale.lang);
    });

    // add new languages to the list
    this.translate.addLangs(this.langIds);
  }

  setLanguage(lang: string): void {
    if (lang) {
      const formattedLang = this.formatLanguageForCookie(lang);
      this.translate.use(lang);
      const cookieValue = `c%3D${formattedLang}%7Cuic%3D${formattedLang}`;
      this.setCookie(this.cookieName, cookieValue);
    }
  }

  getLanguage(): string {
    const cookieValue = this.getCookie(this.cookieName);
    const matches = /c%3D([a-zA-Z-]+)%7Cuic%3D/.exec(cookieValue);
    if (matches) {

      const fullLangCode = matches[1];
      if (fullLangCode === 'en-US') {
        return 'en';
      }
      return fullLangCode;
    }
    return 'en';
  }

  getSavedLanguageOrBrowserDefault(): string {
    return this.getLanguage() || this.translate.getBrowserLang();
  }

  isPolishLanguageSelected(): boolean {
    return isPolishLanguage(this.getCurrentLanguage());
  }

  getCurrentLanguage(): LanguageType {
    return this.translate.currentLang.toLowerCase() as LanguageType;
  }

  getTranslationFor(elemToTranslate: string | string[], params?: object): Observable<string | any> {
    return this.translate.get(elemToTranslate, params);
  }

  getInstantTranslation(elemToTranslate: string | string[]): any {
    return this.translate.instant(elemToTranslate);
  }

  onLanguageChange(): EventEmitter<LangChangeEvent> {
    return this.translate.onLangChange;
  }

  private setCookie(name: string, value: string) {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    const expires = `expires=${date.toUTCString()}`;
    const path = 'path=/';
    const cookieString = `${name}=${value};${expires};${path}`;
    document.cookie = cookieString;
  }

  private getCookie(name: string): string {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return '';
  }

  private formatLanguageForCookie(lang: string): string {
    if (lang === 'en') {
      return 'en-US';
    }
    return lang;
  }
}
